import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@core/services/api/apiRequest";
import type { Action, ActionStatusType } from "../actions.types";
import { shouldUpdateStatus } from "../utils/shouldUpdateStatus";

export type UseUpdateActionStatusArgs = {
  actionId: number;
  status: ActionStatusType;
  currentStatus?: string;
  onDone?: () => Promise<void>;
};

/**
 * Hook to update the status of an action.
 * Uses the /actions/{actionId}/status/ endpoint.
 *
 * When `currentStatus` is provided, the hook checks `shouldUpdateStatus`
 * before making the API call to prevent invalid transitions (e.g. duplicate
 * Completed submissions).
 */
export function useUpdateActionStatus({
  actionId,
  status,
  currentStatus,
  onDone,
}: UseUpdateActionStatusArgs) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (overrides?: { status?: ActionStatusType } | void) => {
      const statusToSend =
        (overrides && "status" in overrides ? overrides.status : undefined) ??
        status;

      // Guard: skip API call if transition is invalid
      if (
        currentStatus &&
        !shouldUpdateStatus(
          { current_status: { name: currentStatus } } as Action,
          statusToSend
        )
      ) {
        return;
      }

      return await apiRequest(`/actions/${actionId}/status/`, "POST", {
        status: statusToSend,
      });
    },
    onError: (error: Error) => {
      throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["actions"],
      });

      await onDone?.();
    },
  });
}

